import React, { Fragment, memo, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Popover, ReactSelectComponents, Text } from '@Sage/hoi-poi-ui';

import { getValueListWithCustomCall } from 'actions/ValueList';
import { BILLBOARD } from 'constants/Entities';
import FieldWrapper from 'containers/components/Fields/FieldWrapper';
import MultiSelectField from 'containers/components/Fields/MultiSelect';
import { getLiteral } from 'utils/getLiteral';

import BillboardAttachmentsField from './Attachments';

const BILLBOARD_ENVIRONMENTS_LIST = 'tblSucursalesBillboard';

const ENVIRONMENTS_FIELD_CONFIG = {
    isCustomValueList: true,
    customValueListParameters: {
        entity: BILLBOARD,
        method: 'getBillboardEnvironmentsWithGroups',
    },
    valueListName: BILLBOARD_ENVIRONMENTS_LIST,
};

function parseSemicolonIds(value) {
    if (value === null || value === undefined || value === '') return [];
    if (Array.isArray(value)) {
        return value.map((item) =>
            String(typeof item === 'object' ? (item.value ?? item.id) : item),
        );
    }
    return String(value)
        .split(';')
        .filter((id) => id !== '');
}

function formatSemicolonIds(ids) {
    if (!ids || ids.length === 0) return null;
    const normalizedIds = ids.map((id) =>
        String(typeof id === 'object' ? (id.value ?? id.id) : id),
    );
    return `;${normalizedIds.join(';')};`;
}

function mapBillboardValueListToOptions(items = []) {
    return items.map((item) => ({
        value: String(item['-id']),
        label: item.descripcion || item.strname || item.name || '',
    }));
}

function collectEnvironmentUsers(environment) {
    let users = [...(environment.users || [])];

    if (environment.environments?.length > 0) {
        const usersMapped = users.reduce((obj, user) => {
            obj[user.id] = true;
            return obj;
        }, {});

        users = environment.environments.reduce((acc, nestedEnvironment) => {
            nestedEnvironment.users?.forEach((user) => {
                if (!usersMapped[user.id]) {
                    usersMapped[user.id] = true;
                    acc.push(user);
                }
            });
            return acc;
        }, users);
    }

    return users;
}

const BillboardEnvironmentChip = memo(({ data, valueList }) => {
    const valueId = data?.value;
    const completeValue = valueList.find((item) => String(item['-id']) === String(valueId));

    const users = useMemo(
        () => (completeValue ? collectEnvironmentUsers(completeValue) : []),
        [completeValue],
    );

    const label = data?.label || completeValue?.descripcion || completeValue?.strname || '';

    if (!completeValue) {
        return <span className="billboard-environment-chip">{label}</span>;
    }

    const popoverContent = (
        <Fragment>
            <div className="fm-billboard-env-popover-header">
                <Text type="subtitle">{label}</Text>
                <Text type="caption">{getLiteral('label_billboard_environments')}</Text>
            </div>
            <div className="fm-billboard-env-popover-list">
                {users.map((user) => (
                    <Text key={user.id} type="caption">{`${user.firstName} ${user.lastName}`}</Text>
                ))}
            </div>
        </Fragment>
    );

    return (
        <span className="billboard-environment-chip">
            <span className="billboard-environment-chip__label">{label}</span>
            <Popover
                className="fm-billboard-environments-popover"
                trigger={['hover']}
                placement="bottomLeft"
                content={popoverContent}
                overlayStyle={{ zIndex: 99999 }}
                overrides={{
                    trigger: { className: 'fm-billboard-environments-popover-trigger' },
                }}
            >
                <span className="billboard-environment-chip__count">{`(${users.length})`}</span>
            </Popover>
        </span>
    );
});

BillboardEnvironmentChip.displayName = 'BillboardEnvironmentChip';

const EnvironmentMultiValueLabel = memo(({ multiValueLabelProps, valueList }) => {
    const { data } = multiValueLabelProps;

    return (
        <ReactSelectComponents.MultiValueLabel {...multiValueLabelProps}>
            <BillboardEnvironmentChip data={data} valueList={valueList} />
        </ReactSelectComponents.MultiValueLabel>
    );
});

EnvironmentMultiValueLabel.displayName = 'EnvironmentMultiValueLabel';

function createEnvironmentMultiValueLabel(valueList) {
    const WrappedLabel = (multiValueLabelProps) => (
        <EnvironmentMultiValueLabel
            multiValueLabelProps={multiValueLabelProps}
            valueList={valueList}
        />
    );
    WrappedLabel.displayName = 'EnvironmentMultiValueLabelWrapper';
    return WrappedLabel;
}

const BillboardEnvironmentsField = memo(
    ({ error, field, firstErrorField, onChange, readOnly, value }) => {
        const dispatch = useDispatch();

        const valueListData = useSelector(
            (state) => state.valueList[BILLBOARD_ENVIRONMENTS_LIST]?.data ?? [],
        );

        const valueList = valueListData;

        const valueListField = useMemo(
            () => ({
                ...ENVIRONMENTS_FIELD_CONFIG,
                customValueListParameters:
                    field.inputAttrs?.customValueListParameters ||
                    ENVIRONMENTS_FIELD_CONFIG.customValueListParameters,
                valueListName: field.inputAttrs?.valueListName || BILLBOARD_ENVIRONMENTS_LIST,
            }),
            [field],
        );

        useEffect(() => {
            dispatch(getValueListWithCustomCall(valueListField));
        }, [dispatch, valueListField]);

        const loadOptions = useCallback(() => {
            if (valueList.length > 0) {
                return Promise.resolve(mapBillboardValueListToOptions(valueList));
            }

            return new Promise((resolve) => {
                dispatch(
                    getValueListWithCustomCall(valueListField, (result) => {
                        resolve(mapBillboardValueListToOptions(result));
                    }),
                );
            });
        }, [dispatch, valueList, valueListField]);

        const options = useMemo(
            () => (valueList.length > 0 ? mapBillboardValueListToOptions(valueList) : undefined),
            [valueList],
        );

        const selectedIds = useMemo(() => parseSemicolonIds(value), [value]);

        const selectedValue = useMemo(() => {
            if (!selectedIds.length || !valueList.length) return undefined;

            const mapped = selectedIds
                .map((id) => {
                    const item = valueList.find((entry) => String(entry['-id']) === String(id));
                    if (!item) return null;
                    return {
                        value: String(item['-id']),
                        label: item.descripcion || item.strname || item.name || '',
                    };
                })
                .filter(Boolean);

            return mapped.length > 0 ? mapped : undefined;
        }, [selectedIds, valueList]);

        const componentOverride = useMemo(
            () => ({
                MultiValueLabel: createEnvironmentMultiValueLabel(valueList),
            }),
            [valueList],
        );

        const handleChange = useCallback(
            (selected) => {
                if (!selected || selected.length === 0) {
                    onChange(null);
                    return;
                }
                const ids = selected.map((id) =>
                    String(typeof id === 'object' ? (id.value ?? id.id) : id),
                );
                onChange(formatSemicolonIds(ids));
            },
            [onChange],
        );

        return (
            <div
                className={classnames('fm-crud-field', 'fm-billboard-environments-field', {
                    [`fm-crud-field__${field.id}`]: !!field.id,
                    'fm-billboard-environments-field--readonly': readOnly,
                })}
            >
                <FieldWrapper
                    error={error || null}
                    isFullWidth
                    label={field.label}
                    labelMode={field.labelMode || 'vertical'}
                    mandatory={field.mandatory}
                >
                    <MultiSelectField
                        allowMinusOneValue
                        autoFocus={firstErrorField}
                        componentOverride={componentOverride}
                        fieldId={field.id}
                        hint={getLiteral('label_selectone')}
                        isClearable={!readOnly}
                        labelPopover={false}
                        loadOptions={loadOptions}
                        onChange={handleChange}
                        options={options}
                        readOnly={readOnly}
                        value={selectedValue}
                    />
                </FieldWrapper>
            </div>
        );
    },
);

BillboardEnvironmentsField.displayName = 'BillboardEnvironmentsField';

BillboardEnvironmentsField.propTypes = {
    error: PropTypes.any,
    field: PropTypes.object.isRequired,
    firstErrorField: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    readOnly: PropTypes.bool,
    value: PropTypes.any,
};

export const getBillboardCustomFields = ({
    data,
    errors,
    field,
    firstErrorField,
    getError,
    isNewEntity,
    changeField,
}) => {
    if (field.id === 'attachments') {
        return <BillboardAttachmentsField key={field.id} data={data} field={field} />;
    }

    if (field.id !== 'environmentsId') return null;

    return (
        <BillboardEnvironmentsField
            key={field.id}
            error={getError(errors[field.id])}
            field={field}
            firstErrorField={firstErrorField === field.id}
            onChange={changeField(field.id)}
            readOnly={field.readOnly ?? !isNewEntity}
            value={data.environmentsId}
        />
    );
};

export default BillboardEnvironmentsField;
